import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import ConfirmModal from "../../../../components/ConfirmModal";
import * as Fonts from "../../../../components/Fonts";
import { LoadingModal } from "../../../../components/LoadingModal";
import { PlusTag } from "../../../../components/PlusTag";
import { SubHeader } from "../../../../components/SubHeader";
import { useAppState } from "../../../../contexts/App";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import { Site } from "../../../../model/site";
import { ApiSiteResponse, BillingInterval, Subscription, SubscriptionDetail, api } from "../../../../services/api";
import { formatDate as formatLocaleDate } from "../../../../services/i18ln/format";
import { formatMonthYear } from "../../../../services/i18ln/format";
import { formatMoney } from "../../../../utils/money";
import { Container, HelpLine, List, Payments, Section, StatusCard } from "./styles";

type Props = {
  setSite: (site: Site) => void;
};

export const SubscriptionDetailPage = ({ setSite }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { appState, setAppState } = useAppState();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<SubscriptionDetail>();
  const [editingSite, setEditingSite] = useState<ApiSiteResponse>();
  const [isReactivateOpen, setReactivateOpen] = useState(false);
  const [isReactivating, setReactivating] = useState(false);
  const [reactivateFailed, setReactivateFailed] = useState(false);

  const isCanceled = !!detail?.canceledAt;

  // Latest on top, whatever order they come in (Stripe lists newest first); the card is the latest one's
  const invoices = useMemo(
    () => [...(detail?.invoices || [])].sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()),
    [detail]
  );
  const lastCard = invoices.find(invoice => invoice.card)?.card;

  const loadInitialData = useCallback(
    async (siteId: string) => {
      const [detail, sites] = await Promise.all([api.getSubscriptionDetail(siteId), api.getSites()]);

      if (!Object.keys(detail).length) {
        navigate("/sites");
        return;
      }
      setEditingSite(sites.find(site => site.siteId === siteId));
      setDetail(detail);
      setLoading(false);
    },
    [navigate]
  );

  // The breadcrumb leads into this Keepo's screens: when arriving from outside it (e.g. the
  // e-mail link), make it the Keepo being edited so those screens open on it
  useEffect(() => {
    if (!editingSite || appState.editingSiteId === editingSite.siteId) {
      return;
    }
    setSite((editingSite.draft || editingSite.site) as Site);
    setAppState({
      ...appState,
      editingSiteId: editingSite.siteId,
      editingSiteModified: false,
      published: !!editingSite.site,
      editingSiteDisabled: !!editingSite.disabled,
      editingSiteSubscription: editingSite.subscription as Subscription,
      editingSiteTrackingIntegrations: editingSite.tracking,
      editingSitePasswordProtected: !!editingSite.passwordProtected,
      editingSiteAgeGated: !!editingSite.ageGate?.enabled,
    });
  }, [appState, editingSite, setAppState, setSite]);

  useEffect(() => {
    const site = new URLSearchParams(location.search).get("site");
    if (!site) {
      navigate("/sites");
    } else {
      loadInitialData(site);
    }
  }, [location, navigate, loadInitialData]);

  if (loading && !detail) {
    return <LoadingModal />;
  }

  const formatDate = (date?: string) => formatLocaleDate(date ?? dayjs(), i18n.language);
  const intervalTitle = (interval: BillingInterval) =>
    t(`pages.subscription.features.plans.${interval === "year" ? "annually" : "monthly"}.title`);
  const plan = detail?.plan;
  const siteTitle = (editingSite?.site || editingSite?.draft)?.title;

  const siteParam = `?site=${encodeURIComponent(editingSite?.siteId || "")}`;
  // A year paid once with Pix or boleto renews by hand: renewing and switching to a card (auto-renew) take
  // the place of the card's actions; there's no other plan to switch to, and the card can only take over once the
  // first boleto is paid. Undoing a cancellation works for both
  // (a Stripe subscription's cancel-at-period-end, or a Pix/boleto year's renewals turned back on)
  const isOneTime = !!detail?.paymentType;
  type ManageAction = { key: string; className?: string; onClick: () => void };
  const cancelAction: ManageAction = {
    key: "cancel",
    className: "danger",
    // Asks why before canceling
    onClick: () => navigate(`/subscription/cancel${siteParam}`),
  };
  const actions: ManageAction[] = isOneTime
    ? [
        ...(detail?.renewal && !isCanceled
          ? [{ key: "renew", className: "positive", onClick: () => navigate(`/subscription/renew${siteParam}`) }]
          : []),
        ...(detail?.awaitingPayment
          ? []
          : [{ key: "toCard", onClick: () => navigate(`/subscription/to-card${siteParam}`) }]),
        { key: "billing", onClick: () => navigate(`/subscription/billing${siteParam}`) },
        isCanceled
          ? { key: "reactivate", className: "positive", onClick: () => setReactivateOpen(true) }
          : cancelAction,
      ]
    : [
        { key: "payment", onClick: () => navigate(`/subscription/payment-method${siteParam}`) },
        { key: "plan", onClick: () => navigate(`/subscription/change-plan${siteParam}`) },
        { key: "billing", onClick: () => navigate(`/subscription/billing${siteParam}`) },
        isCanceled
          ? { key: "reactivate", className: "positive", onClick: () => setReactivateOpen(true) }
          : cancelAction,
      ];

  // Undoes the cancellation (the subscription renews again), then reads the subscription again
  const reactivate = async () => {
    setReactivating(true);
    setReactivateFailed(false);
    try {
      await api.reactivateSubscription(editingSite?.siteId as string);
      // The plan's state changed: the next reads of the Keepos must come from the server
      api.clearCacheSites();
      const fresh = await api.getSubscriptionDetail(editingSite?.siteId as string);
      setDetail(fresh);
      setReactivating(false);
      setReactivateOpen(false);
    } catch {
      // Stays cancelled: the modal stays open, saying so, to try again
      setReactivating(false);
      setReactivateFailed(true);
    }
  };

  return (
    <Container>
      <SubHeader
        title={t("pages.subscriptionDetail.title")}
        crumbs={[{ label: t("pages.plusBenefits.title"), to: "/keepo/plus" }]}
      />

      <Section>
        <StatusCard $canceled={isCanceled}>
          <div className="top">
            <PlusTag>✦ Keepo Plus</PlusTag>
            {siteTitle && <h3 title={siteTitle}>{siteTitle}</h3>}
            {/* Beside the Keepo on desktop; on phones it drops to its own row */}
            {plan && (
              <span className="plan">
                {intervalTitle(plan.interval)} · {formatMoney(plan.amount, plan.currency, i18n.language)}
                {t(`pages.subscription.features.plans.${plan.interval === "year" ? "perYear" : "perMonth"}`)}
              </span>
            )}
            <span className="status">
              {isCanceled ? t("pages.subscriptionDetail.status.canceled") : t("pages.subscriptionDetail.status.active")}
            </span>
          </div>
          {!!detail?.pendingDiscountPercent && !isCanceled && (
            <p className="discount">
              {t("pages.subscriptionDetail.pendingDiscount", { percent: detail.pendingDiscountPercent })}
            </p>
          )}
          <p className="meta">
            {isCanceled
              ? t("pages.subscriptionDetail.endsOn", { date: formatDate(detail?.currentPeriodEnd) })
              : t("pages.subscriptionDetail.renewsOn", { date: formatDate(detail?.currentPeriodEnd) })}
            {lastCard && ` · ${t("pages.subscriptionDetail.card", { last4: lastCard.last4 })}`}
          </p>
        </StatusCard>
      </Section>

      {!!detail && (
        <Section>
          <Fonts.Label>{t("pages.subscriptionDetail.manage.title")}</Fonts.Label>
          <List>
            {actions.map(action => (
              <li key={action.key}>
                <button type="button" className={action.className} onClick={action.onClick}>
                  <span className="text">
                    <span className="title">{t(`pages.subscriptionDetail.manage.${action.key}.title`)}</span>
                    <span className="description">
                      {t(`pages.subscriptionDetail.manage.${action.key}.description`)}
                    </span>
                  </span>
                  <span className="chevron">
                    <ChevronRightIcon width={12} height={12} color="currentColor" />
                  </span>
                </button>
              </li>
            ))}
          </List>
        </Section>
      )}

      {!!invoices.length && (
        <Section id="payments">
          <Fonts.Label>{t("pages.subscriptionDetail.transactions.title")}</Fonts.Label>
          <Payments>
            {invoices.map(
              (
                { createdAt, card, status, amountPaid, amountBeforeDiscount, discountPercent, currency, interval },
                index
              ) => {
                const hadDiscount =
                  amountPaid !== undefined && amountBeforeDiscount !== undefined && amountBeforeDiscount > amountPaid;
                return (
                  <li key={index}>
                    <span className="main">
                      <span className="month">{formatMonthYear(createdAt, i18n.language)}</span>
                      {(interval || amountPaid !== undefined) && (
                        <span className="info">
                          {interval && intervalTitle(interval)}
                          {interval && amountPaid !== undefined && " · "}
                          {/* A discounted charge shows the original price struck through, then what was paid */}
                          {hadDiscount && <s>{formatMoney(amountBeforeDiscount, currency, i18n.language)}</s>}
                          {hadDiscount && " "}
                          {amountPaid !== undefined && (
                            <span className={hadDiscount ? "paid-amount" : undefined}>
                              {formatMoney(amountPaid, currency, i18n.language)}
                            </span>
                          )}
                          {hadDiscount && !!discountPercent && (
                            <> {t("pages.subscriptionDetail.transactions.discount", { percent: discountPercent })}</>
                          )}
                        </span>
                      )}
                    </span>
                    {card && <span className="card">•••• {card.last4}</span>}
                    {status === "paid" && (
                      <span className="pill paid">{t("pages.subscriptionDetail.transactions.paid")}</span>
                    )}
                    {status === "open" && (
                      <span className="pill open">{t("pages.subscriptionDetail.transactions.open")}</span>
                    )}
                  </li>
                );
              }
            )}
          </Payments>
        </Section>
      )}

      {isReactivateOpen && (
        <ConfirmModal
          type="INFO"
          title={t("pages.subscriptionDetail.reactivate.title")}
          descriptions={[
            reactivateFailed
              ? t("pages.subscriptionDetail.reactivate.error")
              : plan
                ? t("pages.subscriptionDetail.reactivate.description", {
                    date: formatDate(detail?.currentPeriodEnd),
                    value: formatMoney(plan.amount, plan.currency, i18n.language),
                  })
                : t("pages.subscriptionDetail.manage.reactivate.description"),
          ]}
          confirmButtonTitle={t("pages.subscriptionDetail.reactivate.confirm")}
          cancelTitle={t("pages.subscriptionDetail.reactivate.cancel")}
          confirming={isReactivating}
          onConfirm={reactivate}
          onCancel={() => {
            setReactivateOpen(false);
            setReactivateFailed(false);
          }}
        />
      )}

      <HelpLine>
        {t("pages.subscriptionDetail.help.text")}{" "}
        {/* Help is a main screen (no back button): tell it where to come back to */}
        <button
          type="button"
          onClick={() =>
            navigate("/help", {
              state: {
                back: { label: t("pages.subscriptionDetail.title"), to: `${location.pathname}${location.search}` },
              },
            })
          }>
          {t("pages.subscriptionDetail.help.link")}
        </button>
      </HelpLine>
    </Container>
  );
};
